import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface MarqueeImage {
  name: string;
  src: string;
  photographer?: string;
}

const DestinationMarquee = () => {
  const [images, setImages] = useState<MarqueeImage[]>([]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const cached = sessionStorage.getItem("marquee_images_v1");
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setImages(parsed);
            return;
          }
        }
        const { data, error } = await supabase.functions.invoke("destination-marquee");
        if (error || !data?.images?.length) return;
        if (mounted) {
          setImages(data.images);
          sessionStorage.setItem("marquee_images_v1", JSON.stringify(data.images));
        }
      } catch (e) {
        console.error("[DestinationMarquee] load failed", e);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  if (images.length === 0) return null;

  // Deterministic tilt per index so it looks scattered but stable across renders
  const tiltFor = (idx: number) => {
    const tilts = [-6, 4, -3, 7, -5, 2, -7, 5, -2, 6, -4, 3];
    return tilts[idx % tilts.length];
  };

  const renderTrack = (keyPrefix: string) => (
    <div className="flex items-center gap-8 pr-8 shrink-0 py-4">
      {images.map((img, idx) => {
        const tilt = tiltFor(idx);
        return (
          <div
            key={`${keyPrefix}-${img.name}-${idx}`}
            className="relative flex-shrink-0 bg-white p-2 pb-8 shadow-elevated transition-transform duration-300 hover:!rotate-0 hover:scale-105 hover:z-10"
            style={{ transform: `rotate(${tilt}deg)` }}
          >
            <div className="relative h-32 w-44 overflow-hidden bg-muted">
              <img
                src={img.src}
                alt=""
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="absolute bottom-1 left-0 right-0 text-center px-2">
              <span className="font-display text-[11px] text-foreground tracking-wide truncate block">
                {img.name}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      className="w-full overflow-hidden bg-primary/5 border-b border-border"
      aria-hidden="true"
      role="presentation"
    >
      <div className="flex animate-marquee whitespace-nowrap will-change-transform w-max">
        {renderTrack("a")}
        {renderTrack("b")}
      </div>
    </div>
  );
};

export default DestinationMarquee;
